import { Link } from "react-router-dom";
import { useIncidents, useCurrentUser } from "@crm/api";
import { ClipboardList, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { cn } from "@crm/utils";

const statusConfig: Record<string, { label: string; color: string; icon: typeof AlertCircle }> = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  assigned: { label: "Assigned", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800", icon: Clock },
  on_hold: { label: "On Hold", color: "bg-gray-100 text-gray-800", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
};

export function MyIncidentsPage() {
  const { data: user } = useCurrentUser();
  const { data, isLoading, error } = useIncidents({ assigned_to: "me" });

  // Separate open and completed incidents
  const openIncidents = data?.results?.filter(
    (i) => !["resolved", "closed"].includes(i.status)
  ) || [];
  const completedIncidents = data?.results?.filter(
    (i) => ["resolved", "closed"].includes(i.status)
  ) || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">
          My Incidents
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Welcome, {user?.first_name}. Here are your assigned incidents.
        </p>
      </div>

      {/* Stats */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-yellow-100 p-3">
              <Clock className="h-5 w-5 text-yellow-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Open</p>
              <p className="text-2xl font-semibold text-foreground">
                {openIncidents.length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-green-100 p-3">
              <CheckCircle className="h-5 w-5 text-green-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-semibold text-foreground">
                {completedIncidents.length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-red-100 p-3">
              <AlertCircle className="h-5 w-5 text-red-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Critical</p>
              <p className="text-2xl font-semibold text-foreground">
                {openIncidents.filter((i) => i.priority === "critical").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load incidents</p>
        </div>
      ) : !data?.results?.length ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">
            No incidents assigned to you yet.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Open Incidents */}
          {openIncidents.length > 0 && (
            <div>
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                Open Incidents
              </h2>
              <div className="space-y-3">
                {openIncidents.map((incident) => {
                  const status = statusConfig[incident.status] || statusConfig.new;
                  const priority = priorityConfig[incident.priority] || priorityConfig.medium;
                  return (
                    <Link
                      key={incident.id}
                      to={`/incidents/${incident.id}`}
                      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground">
                            {incident.title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
                            Created {new Date(incident.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                              priority.color
                            )}
                          >
                            {priority.label}
                          </span>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                              status.color
                            )}
                          >
                            {status.label}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Completed Incidents */}
          {completedIncidents.length > 0 && (
            <div>
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                Completed
              </h2>
              <div className="space-y-3">
                {completedIncidents.slice(0, 5).map((incident) => {
                  const status = statusConfig[incident.status] || statusConfig.new;
                  return (
                    <Link
                      key={incident.id}
                      to={`/incidents/${incident.id}`}
                      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground">
                            {incident.title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Resolved {incident.resolved_at 
                              ? new Date(incident.resolved_at).toLocaleDateString() 
                              : "recently"}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                            status.color
                          )}
                        >
                          {status.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
